import './TermTip.css';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { getTerm, getNaYinTerm, toneColorVar } from '../data/glossary';

/**
 * 命理术语点按释义。
 * - term：术语查询键（如「正官」「帝旺」「天乙贵人」）
 * - nayin：为 true 时走纳音通用释义（任意纳音名都命中）
 * - children：显示内容（默认用 term 文本），保留原色（十神/五行色）
 * 命中术语库才有虚线下划线与点按弹卡；未命中则原样渲染纯文本。
 */
const TermTip = ({ term, nayin = false, children, className = '', style }) => {
  const entry = nayin ? getNaYinTerm() : getTerm(term);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, placement: 'bottom' });
  const triggerRef = useRef(null);
  const popRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const popW = Math.min(280, vw - 24);
    let left = r.left + r.width / 2 - popW / 2;
    left = Math.max(12, Math.min(left, vw - popW - 12));
    // 默认在下方，空间不足则翻到上方
    const below = r.bottom + 8;
    const placement = window.innerHeight - r.bottom < 160 ? 'top' : 'bottom';
    const top = placement === 'bottom' ? below : r.top - 8;
    setCoords({ top, left, width: popW, placement, arrowX: r.left + r.width / 2 - left });
  }, []);

  const toggle = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!entry) return;
    if (!open) place();
    setOpen((v) => !v);
  }, [entry, open, place]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (popRef.current && popRef.current.contains(e.target)) return;
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const onScrollResize = () => close();
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollResize, true);
    window.addEventListener('resize', onScrollResize);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollResize, true);
      window.removeEventListener('resize', onScrollResize);
    };
  }, [open, close]);

  const content = children != null ? children : term;

  if (!entry) {
    return <span className={className} style={style}>{content}</span>;
  }

  return (
    <>
      <span
        ref={triggerRef}
        className={`term-tip-trigger ${className}`}
        style={style}
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggle(e); }}
      >
        {content}
      </span>
      {open && ReactDOM.createPortal(
        <div
          ref={popRef}
          className={`term-tip-pop term-tip-pop--${coords.placement}`}
          style={{
            top: coords.top,
            left: coords.left,
            width: coords.width,
            transform: coords.placement === 'top' ? 'translateY(-100%)' : 'none'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <span className="term-tip-arrow" style={{ left: coords.arrowX }} />
          <div className="term-tip-head">
            <span className="term-tip-name">{entry.name}</span>
            <span className="term-tip-tone" style={{ color: toneColorVar(entry.tone), borderColor: toneColorVar(entry.tone) }}>
              {entry.tone === '中' ? '中性' : entry.tone === '参半' ? '吉凶参半' : entry.tone === '吉' ? '偏吉' : '偏凶'}
            </span>
          </div>
          <div className="term-tip-def">{entry.def}</div>
        </div>,
        document.body
      )}
    </>
  );
};

export default TermTip;
